/** FILA «TIMELAPSE» a planificării: șantierul săptămână cu săptămână, pe
 *  MACHETA UNIFICATĂ (`viewer/macheta`), din programul de execuție (D1: filă
 *  în Planificare; Stefan, 3.11.2026: nu un film, ci macheta clicabilă adusă
 *  la o săptămână).
 *
 *  Ce vine de unde: programul (pachetele cu desfășurarea și fazele lor,
 *  histograma, macaralele) de la `getProgram`; planul de situație și
 *  arhitectura din firul listei, compuse exact ca pe pagina de situație
 *  (`scenaSit`); modelul structurii din vederea complexului
 *  (`getModelComplex`), pe corp × etaj; organizarea de șantier
 *  (`getSantier`). Starea săptămânii o citește `stareMacheta.ts`; macheta,
 *  stratul structurii, groapa și șantierul doar o desenează. Aici stau axa
 *  timpului, HUD-ul, panoul «Săptămâna asta» și straturile. */
import { useEffect, useMemo, useState } from "react";
import SeIncarca from "../../components/SeIncarca";
import { data, numar, useT } from "../../i18n";
import type { Cheie } from "../../i18n";
import * as api from "../../api";
import { listSolutions } from "../../nav/api";
import { useModelComplex } from "../../structure/modelComplex";
import { culoareEtapei, getProgram, nivelInCorp, numeleNivelului, type EtapaProgram, type Program } from "../cost/program";
import { scenariulProgramului } from "../cost/PanouProgram";
import type { Scenarii } from "../cost/scenarii";
import { laOrigineLocala, northUp, origineDesen, regimulArhitecturii, toSiteLayer, toStoreyPlans } from "../canonical";
import type { CanonicalDoc, StoreyPlan } from "../canonical";
import { scenaSit, type ScenaSit } from "../plan/sit3d";
import MachetaUnificata from "../macheta/MachetaUnificata";
import type { StareMacheta, StratAtasat } from "../macheta/tipuri";
import type { StareStructura } from "../../structure/fem/strat3d";
import { cheia, iso, jaloanele, saptamanile, stareaSaptamanii, type StareSaptamana } from "./stare";
import { stareaCladirii, stareaGropilor, stareaSolului, stareaStructurii } from "./stareMacheta";
import { construiesteGroapa } from "./groapa3d";
import { construiesteSantier } from "./santier3d";
import { camioaneleLa, getSantier, type OrganizareSantier } from "./santier";
import PlanOrganizare from "./PlanOrganizare";
import "./timelapse.css";

/** Numele unui nivel al unei unități, cum îl dă programul («etaj 3», «parter»). */
function numeNivel(p: Program, corp: string, nivel: number | null): string {
  if (nivel == null) return "";
  const n = [...p.corpuri, ...(p.subsoluri ?? [])].find((c) => c.cod === corp)?.niveluri.find((x) => x.nivel === nivel);
  // numele din program poartă corpul în față («C1 · etaj 3»): aici stă lângă codul lui, deci fără
  return nivelInCorp(corp, numeleNivelului(n ?? { nivel, nume: null }));
}

const nr = (v: number | null | undefined, zec = 0) => numar(v, { maximumFractionDigits: zec });
const VITEZE = [0.25, 0.5, 1, 2] as const;
const FAZE: Record<string, Cheie> = {
  cofraj: "tl.faza.cofraj", armare: "tl.faza.armare", beton: "tl.faza.beton", decofrare: "tl.faza.decofrare",
  montaj: "tl.faza.montaj", sapatura: "tl.faza.sapatura", fundatii: "tl.faza.fundatii", lucru: "tl.faza.lucru",
};
export type Straturi = { teren: boolean; structura: boolean; zidarie: boolean; fatada: boolean; exterioare: boolean; santier: boolean };
export const STRATURI_IMPLICITE: Straturi = { teren: true, structura: true, zidarie: true, fatada: true, exterioare: true, santier: true };
const STRATURI: { cheie: keyof Straturi; eticheta: Cheie; culoare: string }[] = [
  { cheie: "teren", eticheta: "tl.strat.teren", culoare: "#a98e6b" }, { cheie: "structura", eticheta: "tl.strat.structura", culoare: "#8794a1" },
  { cheie: "zidarie", eticheta: "tl.strat.zidarie", culoare: "#b9937b" }, { cheie: "fatada", eticheta: "tl.strat.fatada", culoare: "#7fb0a5" },
  { cheie: "exterioare", eticheta: "tl.strat.exterioare", culoare: "#8fbf7a" }, { cheie: "santier", eticheta: "tl.strat.santier", culoare: "#f0b400" },
];
/** presetele camerei machetei (`VEDERI`) */
const VEDERI = ["ansamblu", "strada", "curte", "pasare"] as const;
type Vedere = (typeof VEDERI)[number];
/** straturile solului care sunt EXTERIOARE (se sting cu stratul lor) */
const EXTERIOARE = ["rampe", "drumuri", "parcare", "borduri", "trotuare", "zone", "verde", "iarba", "copaci", "mobilier", "vopsea", "masini"] as const;

type Documente = { sitBrut: CanonicalDoc; arh: CanonicalDoc | null; corpuri: { cod: string; spatiu: string; ansamblu?: boolean }[] };
type Compus = { scena: ScenaSit; origine: [number, number]; arh: CanonicalDoc | null; niveluri: StoreyPlan[] };

export default function PanouTimelapse({ projectId, solutionId, sc, saptamanaInitiala = null, modInitial = "3d" }: {
  projectId: string;
  /** soluția cu lista de cantități (cea a programului) */
  solutionId: string | null;
  /** scenariile paginii; lipsă (harnașamentul de probă) → programul de bază */
  sc?: Scenarii | null;
  /** săptămâna la deschidere (de la 1), pentru un link spre un moment al șantierului */
  saptamanaInitiala?: number | null;
  /** vederea la deschidere: macheta 3D sau planul organizării de șantier */
  modInitial?: "3d" | "plan";
}) {
  const t = useT();
  const [program, setProgram] = useState<Program | null>(null);
  const [docs, setDocs] = useState<Documente | null>(null);
  // organizarea de șantier (documentul planificării): lipsă = fără strat de șantier
  const [santier, setSantier] = useState<OrganizareSantier | null>(null);
  const [stareSantier, setStareSantier] = useState<"se_incarca" | "gata" | "lipsa">("se_incarca");
  const [mod, setMod] = useState<"3d" | "plan">(modInitial);
  const [copiat, setCopiat] = useState(false);
  const [solStructura, setSolStructura] = useState<string | null>(null);
  const [eroare, setEroare] = useState<string | null>(null);
  const [sapt, setSapt] = useState(Math.max(0, (saptamanaInitiala ?? 1) - 1));
  const [ruleaza, setRuleaza] = useState(false);
  const [viteza, setViteza] = useState<(typeof VITEZE)[number]>(0.5);
  const [straturi, setStraturi] = useState<Straturi>(STRATURI_IMPLICITE);
  const [vedere, setVedere] = useState<Vedere>("ansamblu");
  const [ales, setAles] = useState<string | null>(null);
  const scenariu = sc ? scenariulProgramului(sc) : null;
  const { model, stare: stareModel } = useModelComplex(projectId, solStructura, !!solStructura);

  // programul (cu desfășurarea, motor ≥ 14) și documentele firului
  useEffect(() => {
    if (!solutionId) return;
    let viu = true;
    setProgram(null); setDocs(null); setEroare(null); setSantier(null); setStareSantier("se_incarca");
    getProgram(projectId, solutionId, { scenariu })
      .then((p) => { if (viu) setProgram(p); })
      .catch((e: unknown) => { if (viu) setEroare(e instanceof api.ApiError ? e.message : String(e)); });
    getSantier(projectId, solutionId, { scenariu })
      .then((d) => { if (viu) { setSantier(d); setStareSantier("gata"); } })
      .catch(() => { if (viu) setStareSantier("lipsa"); });
    listSolutions(projectId).then(async (reg) => {
      const rand = reg.solutii.find((r) => r.id === solutionId);
      const sitId = rand?.lineage?.site_plan ?? (rand?.discipline === "site_plan" ? rand.id : null);
      const arhId = rand?.lineage?.architecture ?? (rand?.discipline === "architecture" ? rand.id : null);
      if (rand?.discipline === "structure") setSolStructura(rand.id);
      if (!sitId) { if (viu) setEroare(t("tl.fara_situatie")); return; }
      const [sitBrut, arh] = await Promise.all([
        api.solutionDocument(projectId, sitId),
        arhId ? api.solutionDocument(projectId, arhId, { parte: "macheta" }).catch(() => null) : Promise.resolve(null),
      ]);
      if (viu) setDocs({ sitBrut: sitBrut as unknown as CanonicalDoc, arh: arh as unknown as CanonicalDoc | null,
                         corpuri: (rand?.structura?.corpuri ?? []).map((c) => ({ cod: c.cod, spatiu: c.spatiu, ansamblu: c.ansamblu })) });
    }).catch((e: unknown) => { if (viu) setEroare(e instanceof api.ApiError ? e.message : String(e)); });
    return () => { viu = false; };
  }, [projectId, solutionId, scenariu, t]);

  // MACHETA, compusă exact ca pe pagina de situație: originea situației,
  // arhitectura adusă la ea, corpurile cu regimul din arhitectură
  const compus = useMemo<Compus | null>(() => {
    if (!docs) return null;
    try {
      const sit = northUp(docs.sitBrut);
      const origine = origineDesen(sit);
      const arh = docs.arh ? northUp(docs.arh) : null;
      const arhLocal = arh ? laOrigineLocala(arh, origine) : null;
      const site0 = toSiteLayer(laOrigineLocala(sit, origine));
      const site = arhLocal ? regimulArhitecturii(site0, arhLocal) : site0;
      if (!site) return null;
      const niveluri = arhLocal ? toStoreyPlans(arhLocal) : [];
      const scena = scenaSit(site, (site.boundary ?? []) as never, niveluri);
      return scena ? { scena, origine, arh, niveluri } : null;
    } catch {
      return null;
    }
  }, [docs]);
  const zTeren = compus ? -compus.scena.ridicare : 0;
  // înălțimea unui nivel: a parterului din model, altfel 3 m
  const hNivel = useMemo(() => {
    const et = (model?.model?.etaje ?? []).find((e) => /parter/i.test(e.nivel));
    return et?.inaltime_m ?? 3;
  }, [model]);

  // STRATURILE timelapse-ului: groapa (din planul de situație și organizare) și
  // șantierul (din organizare); construite o dată, aduse la săptămână mai jos
  const groapa = useMemo(() => (compus && docs && program
    ? construiesteGroapa(docs.sitBrut, compus.origine, zTeren, hNivel,
                         (cod) => program.subsoluri?.find((s) => s.cod === cod)?.niveluri.length ?? 2, santier)
    : null), [compus, docs, program, santier, zTeren, hNivel]);
  const santierStrat = useMemo(() => (compus && docs && santier ? construiesteSantier(docs.sitBrut, compus.origine, zTeren, santier) : null),
                               [compus, docs, santier, zTeren]);
  useEffect(() => () => { groapa?.dispose(); }, [groapa]);
  useEffect(() => () => { santierStrat?.dispose(); }, [santierStrat]);
  const atasateExtra = useMemo(() => [groapa, santierStrat].filter((x): x is NonNullable<typeof x> => !!x) as StratAtasat[], [groapa, santierStrat]);

  const saptamani = useMemo(() => (program ? saptamanile(program) : []), [program]);
  const jaloane = useMemo(() => (program ? jaloanele(program) : []), [program]);
  const stareCrt: StareSaptamana | null = useMemo(
    () => (program && saptamani.length ? stareaSaptamanii(program, saptamani[Math.min(sapt, saptamani.length - 1)]) : null),
    [program, saptamani, sapt]);

  // STAREA MACHETEI în săptămâna asta: clădirea, solul, structura, groapa, șantierul
  const gropi = useMemo(() => (stareCrt && program ? stareaGropilor(stareCrt, program) : {}), [stareCrt, program]);
  const stare = useMemo<StareMacheta | null>(() => {
    if (!stareCrt || !program) return null;
    const sol = stareaSolului(stareCrt, program, straturi.teren && Object.keys(gropi).length > 0) ?? {};
    if (!straturi.exterioare) for (const k of EXTERIOARE) sol[k] = false;
    return { cladire: straturi.fatada ? stareaCladirii(stareCrt, program) : () => false, sol };
  }, [stareCrt, program, straturi, gropi]);
  const stareStr = useMemo<StareStructura | null>(() => {
    if (!stareCrt || !program) return null;
    const s = stareaStructurii(stareCrt, program);
    return (c, e, f) => {
      if (!straturi.structura) return false;
      if ((f === "pereti" || f === "aprins") && !straturi.zidarie) return false;
      return s(c, e, f);
    };
  }, [stareCrt, program, straturi.structura, straturi.zidarie]);
  useEffect(() => { groapa?.aplica(straturi.teren ? gropi : {}); }, [groapa, gropi, straturi.teren]);
  useEffect(() => {
    if (!santierStrat || !stareCrt) return;
    // macaraua crește cu corpul: brațul deasupra ultimului nivel turnat
    santierStrat.aplica(iso(stareCrt.luni), straturi.santier && stareCrt.santier, (u) => {
      const s = stareCrt.structura[u];
      return s ? ((s.gata) + (s.inLucru ? 1 : 0)) * hNivel : zTeren;
    });
  }, [santierStrat, stareCrt, straturi.santier, hNivel, zTeren]);

  // redarea: o săptămână la `viteza` secunde
  useEffect(() => {
    if (!ruleaza || !saptamani.length) return;
    const id = window.setInterval(() => setSapt((w) => (w + 1 >= saptamani.length ? (setRuleaza(false), w) : w + 1)), viteza * 1000);
    return () => window.clearInterval(id);
  }, [ruleaza, viteza, saptamani.length]);

  if (!solutionId) return <div className="sg-error">{t("bc.ui.fara_solutie")}</div>;
  if (eroare) return <div className="sg-error">{eroare}</div>;
  const gataModel = !solStructura || stareModel === "gata" || stareModel === "lipsa";
  const gataSantier = stareSantier !== "se_incarca";
  if (!program || !docs || !gataModel || !gataSantier) return <div><SeIncarca /><div className="tl-sub" style={{ padding: "6px 0" }}>{t("tl.se_incarca")}</div></div>;
  if (!compus) return <div className="sg-error">{t("tl.fara_situatie")}</div>;
  const luni = saptamani[Math.min(sapt, saptamani.length - 1)];
  const duminica = new Date(luni); duminica.setDate(duminica.getDate() + 6);
  const st = stareCrt!;
  const total = saptamani.length;
  const lunaNr = Math.max(1, Math.floor((duminica.getTime() - new Date(program.start + "T00:00:00").getTime()) / (30.4375 * 86400000)) + 1);
  const pachetAles = ales ? st.pachete.get(ales) : null;
  const macarale = Object.values(st.macarale).reduce((a, b) => a + b, 0);
  const camioane = santier ? camioaneleLa(santier, iso(luni)) : null;
  const costTotal = program.flux?.total_direct_eur ?? 0;
  const laJalon = (d: Date) => setSapt(Math.max(0, saptamani.findIndex((x) => x.getTime() >= d.getTime())));
  // LINK SPRE SĂPTĂMÂNA ASTA: pagina deschide fila și săptămâna din adresă
  const copiazaLink = () => {
    const u = new URL(location.href); u.searchParams.set("fila", "timelapse"); u.searchParams.set("sapt", String(sapt + 1));
    void navigator.clipboard?.writeText(u.toString()).then(() => { setCopiat(true); setTimeout(() => setCopiat(false), 2000); });
  };
  // benzile axei: etapa dominantă a fiecărei săptămâni, din histogramă (fapt al programului)
  const benzi = program.histograma.map((h) => {
    const [etapa] = Object.entries(h.pe_etapa).sort((a, b) => b[1] - a[1])[0] ?? [null];
    return { start: h.start, etapa };
  });

  return (
    <div className="tl">
      <div className="pg-bara tl-bara">
        <div className="sc-tabs">
          <button type="button" className={`sc-tab ${mod === "3d" ? "active" : ""}`} onClick={() => setMod("3d")}>{t("tl.mod.3d")}</button>
          <button type="button" className={`sc-tab ${mod === "plan" ? "active" : ""}`} disabled={!santier}
                  title={santier ? undefined : t("tl.fara_organizare")} onClick={() => setMod("plan")}>{t("tl.mod.plan")}</button>
        </div>
        <span className="despart" />
        <span className="ob-lbl">{t("tl.vedere")}</span>
        {VEDERI.map((v) => (
          <button key={v} type="button" className={`btn btn-mic ${vedere === v ? "on" : ""}`} onClick={() => setVedere(v)}>
            {t(`viz.cam.${v}` as Cheie)}</button>
        ))}
        <span className="despart" />
        <span className="ob-lbl">{t("tl.straturi")}</span>
        {STRATURI.map((s) => (
          <button key={s.cheie} type="button" className={`ip-pill tl-strat ${straturi[s.cheie] ? "on" : ""}`}
                  aria-pressed={straturi[s.cheie]}
                  onClick={() => setStraturi({ ...straturi, [s.cheie]: !straturi[s.cheie] })}>
            <i style={{ background: s.culoare }} />{t(s.eticheta)}</button>
        ))}
        <span style={{ flex: 1 }} />
        {stareSantier === "lipsa" && <span className="ip-pill ip-pill-amb">{t("tl.fara_organizare")}</span>}
        {!model && solStructura && <span className="ip-pill ip-pill-amb">{t("tl.fara_model")}</span>}
        {!solStructura && <span className="ip-pill ip-pill-amb">{t("tl.fara_structura")}</span>}
      </div>

      <div className="tl-scena">
        <div className="tl-panza">
          <MachetaUnificata projectId={projectId} scena={compus.scena} docArh={compus.arh} planuri={compus.niveluri}
                            structura={stareModel === "gata" ? model : null} origine={compus.origine} solutie={solStructura} corpuri={docs.corpuri}
                            perNivel stare={stare} stareStructurii={stareStr} atasateExtra={atasateExtra} faraComutatoare vedere={vedere} />
        </div>
        {mod === "plan" && santier && <PlanOrganizare sit={docs.sitBrut} doc={santier} program={program} luni={iso(luni)} />}
        <aside className="tl-hud">
          <div className="ob-lbl">{t("tl.saptamana_din", { n: sapt + 1, t: total })}</div>
          <div className="tl-data">{data(iso(luni))} – {data(iso(duminica))}</div>
          <div className="tl-sub">{t("tl.luna_din", { n: lunaNr, t: Math.ceil(program.luni) })}</div>
          <div className="tl-kpi">
            <div><span className="ob-lbl">{t("tl.oameni")}</span><b>{nr(st.oameni)}</b></div>
            <div><span className="ob-lbl">{t("tl.macarale")}</span><b>{macarale} <small>/ {program.macarale.plafon}</small></b></div>
            <div><span className="ob-lbl">{t("tl.camioane")}</span><b>{camioane ? nr(camioane.pe_zi) : "—"}</b></div>
            <div><span className="ob-lbl">{t("tl.cost")}</span><b>{costTotal ? `${nr(100 * st.cost_eur / costTotal)} %` : "—"}</b>
              <small>{nr(st.cost_eur / 1e6, 1)} / {nr(costTotal / 1e6, 1)} M€</small></div>
          </div>
          <div className="ob-lbl" style={{ marginTop: 10 }}>{t("tl.stadiul_structurii")}</div>
          {program.corpuri.map((c) => {
            const s = st.structura[c.cod]; const n = c.niveluri.filter((x) => !x.subsol).length || 1;
            const gata = s?.gata ?? 0; const inLucru = s?.inLucru;
            return (
              <div key={c.cod} className="tl-corp">
                <b>{c.cod}</b>
                <span className="tl-bara-corp"><span style={{ width: `${(100 * (gata + (inLucru?.f ?? 0))) / n}%` }} /></span>
                <small>{inLucru ? `${numeNivel(program, c.cod, inLucru.nivel)} · ${t(FAZE[inLucru.faza ?? "lucru"])}` : gata >= n ? t("tl.gata") : gata ? numeNivel(program, c.cod, gata - 1) : "—"}</small>
              </div>
            );
          })}
        </aside>
        <aside className="tl-panou">
          <div className="tl-panou-cap">
            <b>{t("tl.saptamana_asta")}</b>
            <span className="ip-pill ip-pill-mut">{t("tl.pachete_n", { n: st.active.length })}</span>
          </div>
          <div className="tl-lista">
            {st.active.length === 0 && <div className="tl-gol">{t("tl.nimic")}</div>}
            {[...st.active].sort((a, b) => a.corp.localeCompare(b.corp) || (a.nivel ?? -99) - (b.nivel ?? -99)).map((e) => {
              const s = st.pachete.get(cheia(e))!.s;
              return (
                <button key={cheia(e)} type="button" className={`tl-pachet ${ales === cheia(e) ? "ales" : ""}`}
                        onClick={() => setAles(ales === cheia(e) ? null : cheia(e))}>
                  <i style={{ background: culoareEtapei(e.etapa) }} />
                  <span className="n">{e.corp}{e.nivel != null ? ` · ${numeNivel(program, e.corp, e.nivel)}` : ""} · {e.nume}</span>
                  <span className="m">{data(e.start)} → {data(e.sfarsit)}{s.faza && s.faza !== "lucru" ? ` · ${t(FAZE[s.faza])}` : ""} · {nr(100 * Math.min(s.f, 1))} %</span>
                  <span className="o">{nr(Object.values(e.muncitori).reduce((a, b) => a + b, 0))}</span>
                </button>
              );
            })}
          </div>
          {pachetAles && <Detaliu e={pachetAles.e} f={pachetAles.s.f} nume={numeNivel(program, pachetAles.e.corp, pachetAles.e.nivel)} />}
          {!pachetAles && program.explicatii?.[0] && <div className="tl-explicatie">{program.explicatii[0]}</div>}
        </aside>
      </div>

      <div className="tl-axa">
        <div className="tl-comenzi">
          <button type="button" className="btn btn-mic" aria-label={t("tl.la_inceput")} onClick={() => setSapt(0)}>⏮</button>
          <button type="button" className={`btn btn-mic ${ruleaza ? "on" : ""}`} aria-label={t(ruleaza ? "tl.pauza" : "tl.porneste")}
                  onClick={() => setRuleaza(!ruleaza)}>{ruleaza ? "❚❚" : "▶"}</button>
          <button type="button" className="btn btn-mic" aria-label={t("tl.la_sfarsit")} onClick={() => setSapt(total - 1)}>⏭</button>
          <select value={viteza} onChange={(ev) => setViteza(Number(ev.target.value) as (typeof VITEZE)[number])} aria-label={t("tl.viteza")}>
            {VITEZE.map((v) => <option key={v} value={v}>{t("tl.viteza_v", { s: v })}</option>)}
          </select>
          <button type="button" className="btn btn-mic tl-btn-text" onClick={copiazaLink} title={t("tl.link.titlu")}>{copiat ? t("tl.link.copiat") : t("tl.link")}</button>
        </div>
        <div className="tl-cursor">
          <input type="range" min={0} max={Math.max(total - 1, 0)} value={sapt} onChange={(ev) => setSapt(Number(ev.target.value))}
                 aria-label={t("tl.saptamana")} />
          <div className="tl-benzi" aria-hidden="true">
            {benzi.map((b, i) => <span key={b.start} style={{ left: `${(100 * i) / Math.max(benzi.length, 1)}%`, width: `${100 / Math.max(benzi.length, 1)}%`,
                                                                   background: b.etapa ? culoareEtapei(b.etapa) : "transparent" }} />)}
          </div>
          <div className="tl-jaloane">
            {jaloane.map((j, i) => {
              const k = saptamani.findIndex((x) => x.getTime() >= j.luni.getTime());
              return (
                <button key={i} type="button" className="tl-jalon" style={{ left: `${(100 * Math.max(k, 0)) / Math.max(total - 1, 1)}%` }}
                        title={`${t(j.cheie as Cheie, { u: j.unitate ?? "" })} · ${data(iso(j.luni))}`} onClick={() => laJalon(j.luni)}>
                  <i /></button>
              );
            })}
          </div>
        </div>
        <div className="tl-legenda pg-legenda">
          {["E10", "E30", "E40", "E90", "E70", "E99"].map((e) => (
            <span key={e} className="pg-dot"><i style={{ background: culoareEtapei(e) }} />{t(`tl.leg.${e}` as Cheie)}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Detaliul pachetului apăsat: ce e, cât e făcut, fazele și cantitățile lui.
 *  Fazele suprapuse (decofrarea verticalelor, sub lucrul plăcii) nu intră în
 *  banda de lucru: ea arată succesiunea, nu așteptările. */
function Detaliu({ e, f, nume }: { e: EtapaProgram; f: number; nume: string }) {
  const t = useT();
  const cant: [Cheie, number | undefined, string][] = [
    ["tl.cant.beton", e.beton_m3, "m³"], ["tl.cant.cofraj", e.cofraj_m2, "m²"], ["tl.cant.otel", e.otel_kg, "kg"],
    ["tl.cant.sapatura", e.sapatura_m3, "m³"], ["tl.cant.umplutura", e.umplutura_m3, "m³"],
  ];
  const faze = (e.faze ?? []).filter((x) => !(x as { suprapus?: boolean }).suprapus);
  return (
    <div className="tl-detaliu">
      <b>{e.corp}{nume ? ` · ${nume}` : ""} · {e.nume}</b>
      <div>{data(e.start)} → {data(e.sfarsit)} · {nr(e.zile, 1)} {t("tl.zl")} · {nr(100 * Math.min(f, 1))} %</div>
      {faze.length > 1 && (
        <div className="tl-faze">
          {faze.map((x, i) => (
            <span key={`${x.faza}-${i}`} className={f > x.de_la ? "on" : ""} style={{ flex: Math.max(x.pana_la - x.de_la, 0.05) }}
                  title={(x as { parte?: string }).parte ? `${t(FAZE[x.faza])} · ${t(`prog.parte.${(x as { parte?: string }).parte}` as Cheie)}` : undefined}>
              {t(FAZE[x.faza])}</span>
          ))}
        </div>
      )}
      <div className="tl-cant">
        {cant.filter(([, v]) => v).map(([k, v, um]) => <span key={k}>{t(k)}: <b>{nr(v!)}</b> {um}</span>)}
      </div>
    </div>
  );
}
